import React from 'react'
import { useTokenExpiration } from '../../utils/token'

// Session card component showing token expiration and user info

const SessionCard = ({ token, userEmail, userType, onLogout, onRenew, isRenewing }) => {
    const { timeRemaining, totalTokenTime, isExpiring } = useTokenExpiration(token, onLogout);

    if (!timeRemaining) return null;

    const totalSeconds = Math.ceil(timeRemaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const totalMinutes = minutes + (seconds > 0 ? 1 : 0);

    // Calcular progresso: começa em 100% e reduz conforme tempo passa
    // timeRemaining / totalTokenTime = quanto % ainda falta
    const progressPercent = totalTokenTime
        ? Math.max(0, Math.min(100, (timeRemaining / totalTokenTime) * 100))
        : Math.max(0, Math.min(100, (timeRemaining / (30 * 60 * 1000)) * 100)); // Fallback 30 min

    let timeText = '';
    if (minutes > 0) {
        timeText = `${minutes}m ${seconds}s`;
    } else if (seconds > 0) {
        timeText = 'Menos de 1 minuto';
    } else {
        timeText = 'Expirando agora...';
    }

    // Cores dinamicamente baseadas no tempo restante
    const containerColor = isExpiring
        ? 'bg-red-50/70 dark:bg-red-950/20 border-red-200/70 dark:border-red-800/30'
        : 'bg-gray-50/70 dark:bg-gray-900/30 border-gray-200/70 dark:border-gray-700/60';

    const avatarColor = isExpiring
        ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
        : 'bg-blue-50 dark:bg-blue-900/25 text-blue-600 dark:text-blue-400';

    const timeColor = isExpiring
        ? 'text-red-600 dark:text-red-400 font-medium'
        : 'text-gray-500 dark:text-gray-400';

    const barColor = isExpiring
        ? 'bg-red-500'
        : 'bg-blue-500';

    return (
        <div className={`${containerColor} border rounded-xl p-3 space-y-2.5 transition-colors`}>
            {/* Header: User Info and Logout */}
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${avatarColor}`}>
                        <span className="material-icons" style={{ fontSize: '17px' }}>account_circle</span>
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                            <p className="text-[13px] font-medium text-gray-800 dark:text-gray-100 truncate">{userEmail || 'Usuário'}</p>
                            {(userType === 'env_admin' || userType === 'invited') && (
                                <span className="flex-shrink-0 px-1.5 py-0.5 bg-green-50 dark:bg-green-900/25 text-green-700 dark:text-green-400 rounded text-[10px] font-semibold whitespace-nowrap">
                                    {userType === 'env_admin' ? 'admin' : 'convidado'}
                                </span>
                            )}
                        </div>
                        <p className={`text-[11px] ${timeColor}`}>
                            {isExpiring ? `Expira em ${timeText}` : `${totalMinutes}m restante(s)`}
                        </p>
                    </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                    {isExpiring && onRenew && (
                        <button
                            onClick={onRenew}
                            disabled={isRenewing}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-40 transition"
                            title="Renovar sessão"
                        >
                            <span className="material-icons" style={{ fontSize: '16px' }}>refresh</span>
                        </button>
                    )}
                    <button onClick={onLogout} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition" title="Sair">
                        <span className="material-icons" style={{ fontSize: '16px' }}>logout</span>
                    </button>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200/70 dark:bg-gray-700/50 rounded-full h-1 overflow-hidden">
                <div
                    className={`h-full ${barColor} rounded-full transition-all duration-1000`}
                    style={{ width: `${Math.max(0, progressPercent)}%` }}
                ></div>
            </div>

            {/* Mensagem de Aviso */}
            {isExpiring && (
                <div className="flex items-start gap-1.5 text-[11px] text-red-600 dark:text-red-400">
                    <span className="material-icons flex-shrink-0" style={{ fontSize: '13px', marginTop: '1px' }}>warning</span>
                    <p><strong className="font-semibold">Salve seu trabalho!</strong> Você será redirecionado para o login em breve.</p>
                </div>
            )}
        </div>
    );
};

export default SessionCard
